import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { ArticleCard } from "../components/ArticleCard";
import { ContactCTA } from "../components/ContactCTA";
import { asList, useFetch } from "../lib/api";

const Pill = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${active ? "border-teal bg-teal text-mist" : "border-teal/25 text-teal hover:border-teal"}`}
  >
    {children}
  </button>
);

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

export default function Insights() {
  const { data, loading } = useFetch("/articles");
  const articles = asList(data);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const categories = useMemo(() => [...new Set(articles.map((a) => a.category).filter(Boolean))], [articles]);
  const term = q.trim().toLowerCase();
  const filtered = articles.filter(
    (a) =>
      (category === "all" || a.category === category) &&
      (!term || `${a.title} ${a.excerpt}`.toLowerCase().includes(term))
  );

  return (
    <main data-testid="insights-page">
      <PageHeader
        testId="insights-header"
        eyebrow="Insights & news"
        lines={["Knowledge that", "travels to the field."]}
        intro="Technical insights on the latest research and innovation in sustainable crop protection, with a focus on extension and knowledge transfer for growers, agronomists, advisors and product group representatives."
      />
      <section className="bg-paper py-16 sm:py-24">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <div className="space-y-6 border-b border-teal/15 pb-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                <Pill active={category === "all"} onClick={() => setCategory("all")} testId="article-category-all">All topics</Pill>
                {categories.map((c) => (
                  <Pill key={c} active={category === c} onClick={() => setCategory(c)} testId={`article-category-${slug(c)}`}>{c}</Pill>
                ))}
              </div>
              <label className="flex items-center gap-3 border-b border-teal/30 pb-2 lg:w-80">
                <Search className="h-4 w-4 text-teal" />
                <input data-testid="article-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search insights" className="w-full bg-transparent text-sm text-teal outline-none placeholder:text-teal/60" />
              </label>
            </div>
          </div>
          <div className="mt-16 grid gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a, i) => (
              <ArticleCard key={a.id} article={a} index={i} featured={i === 0 && category === "all" && !term} />
            ))}
          </div>
          {!loading && filtered.length === 0 && (
            <p data-testid="articles-empty" className="py-20 text-center font-display text-2xl text-teal/70">No insights match your filters yet.</p>
          )}
        </div>
      </section>
      <ContactCTA />
    </main>
  );
}
